import React from 'react';
import { WeezHeader, Message, Button, Icon, SplitContainer, ToastContext } from '@weezevent/nacre';
import EventSettingsModel from '../../../models/eventSettings';
import { useTranslation } from 'react-i18next';
import { useForm } from '../../../utils/validators';

import Option from './Option';

import './notification.scss';

export default function NotificationSettings({ organization, event }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [initialValues, setInitialValues] = React.useState({});
    const [submitting, setSubmitting] = React.useState(false);

    const [form, setForm, setFormObject] = useForm({
        toggle_perso_addresses: false,
        perso_addresses: [],
        toggle_sales_report_addresses: false,
        sales_report_addresses: [],
        select_periodicity: '1'
    });

    const valuesNotModified = React.useMemo(() => {
        return JSON.stringify(initialValues) === JSON.stringify(form);
    }, [form, initialValues]);

    const onSalesReportAdresseToggle = React.useCallback(
        value => {
            if (!value) {
                // value 0 disables email reporting.
                setForm('select_periodicity', '0');
            }
            if (form['select_periodicity'] === '0') {
                // value 1 corresponds to a daily report.
                setForm('select_periodicity', '1');
            }
            setForm('toggle_sales_report_addresses', !form['toggle_sales_report_addresses']);
        },
        [form, setForm]
    );
    const onPersoAdresseToggle = React.useCallback(() => setForm('toggle_perso_addresses', !form['toggle_perso_addresses']), [form, setForm]);

    const frequencyOptions = React.useMemo(() => {
        return [
            { text: t('notifications.frequency.daily'), value: '1' },
            { text: t('notifications.frequency.weekly'), value: '7' }
        ];
    }, []);

    const [notificationSettings, loading, sync] = EventSettingsModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            cache: false,
            onSuccess: settings => {
                const _Addresses = settings.values.adresse_confirmation === '' ? [] : settings.values.adresse_confirmation.split(',');
                const _SalesAddresses =
                    settings.values.sales_report_adresse === '' || settings.values.sales_report_adresse === '0' ? [] : settings.values.sales_report_adresse.split(',');
                const _form = {
                    toggle_perso_addresses: Boolean(parseInt(settings.values.perso_adresse)),
                    perso_addresses: _Addresses,
                    toggle_sales_report_addresses: _SalesAddresses.length > 0 && settings.values.sales_report_periodicity !== '0',
                    sales_report_addresses: _SalesAddresses,
                    select_periodicity: settings.values.sales_report_periodicity
                };
                setFormObject(_form);
                setInitialValues(_form);
            }
        },
        [organization, event, setFormObject]
    );

    const onSubmit = React.useCallback(() => {
        setSubmitting(true);
        const newSettings = new EventSettingsModel({
            ...notificationSettings,
            values: {
                ...notificationSettings.values,
                sales_report_periodicity: form['select_periodicity'],
                perso_adresse: form['toggle_perso_addresses'] ? '1' : '0',
                sales_report_adresse: form['toggle_sales_report_addresses'] ? form['sales_report_addresses'].join(',') : '0',
                adresse_confirmation: !form['toggle_perso_addresses'] ? '' : form['perso_addresses'].join(',')
            }
        });
        newSettings
            .save({
                organizationId: organization.pk,
                eventId: event.pk
            })
            .then(_ => {
                toast.success(t('ticket.event.integration.success-toast'));
                sync();
            })
            .catch(err => {
                console.error('Error', err);
                toast.error(t('ticket.event.integration.failure-toast'));
            })
            .finally(() => {
                setSubmitting(false);
            });
    }, [organization, event, toast, notificationSettings, form, sync]);

    const disableButton = React.useMemo(() => {
        return (
            valuesNotModified ||
            (form['toggle_sales_report_addresses'] && !form['sales_report_addresses'].length) ||
            (form['toggle_perso_addresses'] && !form['perso_addresses'].length)
        );
    }, [valuesNotModified, form]);

    return (
        <>
            <WeezHeader
                title={t('notifications.main_title')}
                rightComponent={[
                    <Button
                        primary
                        key="primary"
                        icon={<Icon className="cta-save-icon" name="save" margin={false} />}
                        isLoading={submitting}
                        disabled={disableButton}
                        label={t('ticket.common.action.save')}
                        onClick={onSubmit}
                    />
                ]}
            >
                {t('notifications.main_subtitle')}
            </WeezHeader>

            <SplitContainer
                additional={
                    <Message type="tips" title={t('notifications.tips_title')} mini>
                        <p>{t('notifications.tips')}</p>
                        <p>{t('notifications.other_tips')}</p>
                    </Message>
                }
            >
                <Option
                    select
                    name="sales_report_addresses"
                    title={t('notifications.main_orders_card_title')}
                    subTitle={t('notifications.main_orders_card_subtitle')}
                    toggleTitle={t('notifications.order_notification_toggle')}
                    loading={loading || submitting}
                    organization={organization}
                    options={frequencyOptions}
                    value={form['select_periodicity']}
                    addresses={form['sales_report_addresses']}
                    setForm={setForm}
                    toggle={form['toggle_sales_report_addresses']}
                    onToggle={onSalesReportAdresseToggle}
                />

                <Option
                    switch
                    name="perso_addresses"
                    title={t('notifications.main_card_title')}
                    subTitle={t('notifications.main_card_subtitle')}
                    toggleTitle={t('notifications.email_notification_toggle')}
                    loading={loading || submitting}
                    organization={organization}
                    addresses={form['perso_addresses']}
                    setForm={setForm}
                    toggle={form['toggle_perso_addresses']}
                    onToggle={onPersoAdresseToggle}
                />
            </SplitContainer>
        </>
    );
}
